// Item-effect runtime: runs ItemEffects from equipment, mutations, synergies (and upgrades)
// on their triggers, aggregates behaviour flags, and announces synergy changes.
//
// Triggers come from the bus (see ARCHITECTURE "Item effects"); each effect has its own
// cooldown and chance (ctx.rng). Effect damage carries noTrigger so effects never recurse.

import { TEX } from '../../art/keys';
import { COLORS } from '../../config/palette';
import type { EffectRuntimeApi, EnemyApi, FxApi, GameContext, PlayerApi } from '../../core/contracts';
import { Subscriptions } from '../../core/events';
import type { DamageInfo, EffectAction, ItemEffect, SynergyId, Trigger } from '../../core/types';
import { equipDef, mutationDef, mutationFlags, synergyDef, synergyFlags, upgradeFlags } from './data';
import { TRAIL_DEFAULT_STATUS, implicitTrailEffect } from './defaults';
import { enemyList, livePlayer, playerStats, targetable, withTag, type ProjectileSpecEx } from './internal';
import type { CombatSfx } from './sfx';

/** Prefix for "already announced" synergy ids stored in run.flags (survives room restarts). */
export const SYNERGY_FLAG_PREFIX = 'syn:';
/** player:land impacts below this do not fire onLand. */
const LAND_IMPACT = 900;
/** Re-entrancy guard: an effect firing from inside another effect's damage. */
const MAX_DEPTH = 2;
/** Dash trails run this far above the feet (the dash streak's height). */
const TRAIL_GROUND_LIFT = 14;

interface RtEffect {
  key: string;
  effect: ItemEffect;
  tickT: number;
}

interface TriggerData {
  target?: EnemyApi;
  x?: number;
  y?: number;
  info?: DamageInfo;
  /** onDashEnd path. */
  path?: { x0: number; y0: number; x1: number; y1: number };
}

export function createEffects(ctx: GameContext, fx: FxApi, sfx: CombatSfx): EffectRuntimeApi {
  const subs = new Subscriptions();
  const byTrigger = new Map<Trigger, RtEffect[]>();
  const ticking: RtEffect[] = [];
  /** Cooldown end times (ctx.time.now) by effect key; survives rebuilds within the room. */
  const cooldownUntil = new Map<string, number>();
  const flags = new Set<string>();
  let synergies: SynergyId[] = [];
  let built = false;
  let depth = 0;
  let started = false;
  let destroyed = false;
  const warnedCustom = new Set<string>();

  const now = (): number => {
    try {
      return ctx.time.now;
    } catch {
      return performance.now() / 1000;
    }
  };

  function collect(): { key: string; effect: ItemEffect }[] {
    const out: { key: string; effect: ItemEffect }[] = [];
    flags.clear();
    const inv = ctx.run.inventory;
    const kit = ctx.run.mech.kitTags;

    for (const eq of Object.values(inv.equipment)) {
      if (!eq) continue;
      const def = equipDef(eq.defId);
      if (!def) continue;
      const effects = def.effects ?? [];
      effects.forEach((effect, i) => out.push({ key: `eq:${def.id}:${i}`, effect }));
      // Legs that declare a burning / electric trail but no trail effect still lay one.
      if (def.dashTrail && (def.dashTrail === 'burn' || def.dashTrail === 'electric') && !effects.some((e) => e.action.type === 'trail')) {
        out.push({ key: `eq:${def.id}:trail`, effect: implicitTrailEffect(def.id, def.dashTrail) });
      }
    }
    for (const m of inv.mutations) {
      const def = mutationDef(m.defId);
      (def?.effects ?? []).forEach((effect, i) => out.push({ key: `mut:${m.defId}:${i}`, effect }));
      for (const f of mutationFlags(m.defId)) flags.add(f);
    }
    let active: SynergyId[] = [];
    try {
      active = inv.activeSynergies(kit);
    } catch (err) {
      console.warn('[combat] activeSynergies failed', err);
    }
    synergies = active.slice();
    for (const id of active) {
      const def = synergyDef(id);
      (def?.effects ?? []).forEach((effect, i) => out.push({ key: `syn:${id}:${i}`, effect }));
      for (const f of synergyFlags(id)) flags.add(f);
    }
    for (const u of inv.upgrades) for (const f of upgradeFlags(u)) flags.add(f);
    return out;
  }

  function rebuildList(): void {
    const prevTicks = new Map<string, number>();
    for (const r of ticking) prevTicks.set(r.key, r.tickT);
    byTrigger.clear();
    ticking.length = 0;
    let list: { key: string; effect: ItemEffect }[] = [];
    try {
      list = collect();
    } catch (err) {
      console.warn('[combat] effect rebuild failed', err);
    }
    for (const { key, effect } of list) {
      if (!effect || !effect.action) continue;
      const rt: RtEffect = { key, effect, tickT: prevTicks.get(key) ?? Math.max(0.5, effect.interval ?? 1) };
      if (effect.trigger === 'tick') ticking.push(rt);
      let arr = byTrigger.get(effect.trigger);
      if (!arr) byTrigger.set(effect.trigger, (arr = []));
      arr.push(rt);
    }
    built = true;
  }

  /** Announce synergies that became active (once per run) and retire ones that were lost. */
  function diffSynergies(): void {
    const runFlags = ctx.run.flags;
    const active = new Set(synergies);
    for (const id of synergies) {
      const k = SYNERGY_FLAG_PREFIX + id;
      if (runFlags.has(k)) continue;
      runFlags.add(k);
      const def = synergyDef(id);
      ctx.bus.emit('synergy:activated', { id });
      ctx.bus.emit('ui:banner', { title: def?.name ?? id, subtitle: def?.desc, color: def?.color ?? COLORS.orbUnstable });
      sfx.play('synergy', { volume: 0.9 });
    }
    for (const k of Array.from(runFlags)) {
      if (!k.startsWith(SYNERGY_FLAG_PREFIX)) continue;
      const id = k.slice(SYNERGY_FLAG_PREFIX.length) as SynergyId;
      if (active.has(id)) continue;
      runFlags.delete(k);
      ctx.bus.emit('synergy:deactivated', { id });
    }
  }

  function inCombat(): boolean {
    try {
      if (ctx.world?.state === 'combat') return true;
    } catch {
      // world unavailable
    }
    try {
      return !!ctx.bosses?.active;
    } catch {
      return false;
    }
  }

  function playerCentre(p: PlayerApi): { x: number; y: number } {
    return { x: p.x, y: p.y - 8 };
  }

  /** Run one action. Returns false if it could not do anything (cooldown is then not consumed). */
  function run(rt: RtEffect, a: EffectAction, data: TriggerData): boolean {
    const p = livePlayer(ctx);
    if (!p || !p.alive) return false;
    const stats = playerStats(ctx);
    const target = data.target && data.target.alive ? data.target : undefined;
    const cx = target?.x ?? data.x ?? p.x;
    const cy = target?.y ?? data.y ?? p.y;
    switch (a.type) {
      case 'explode': {
        ctx.damage.explode(
          cx,
          cy,
          a.radius * stats.explosionRadius,
          { amount: a.damage, tags: withTag(a.tags, 'explosive'), source: 'player', noTrigger: true, knockback: 340, label: rt.key },
          { color: COLORS.explosive },
        );
        return true;
      }
      case 'chain': {
        const jumps = Math.max(0, a.count - 1 + Math.round(stats.chainBonus));
        const from = target ? { x: target.x, y: target.y } : playerCentre(p);
        const hit = ctx.damage.chainLightning(from, null, {
          count: jumps,
          range: a.range,
          damage: a.damage,
          exclude: target ? new Set([target]) : undefined,
          noTrigger: true,
          label: rt.key,
        } as Parameters<typeof ctx.damage.chainLightning>[2]);
        return hit.length > 0;
      }
      case 'shockwave': {
        const x = p.x;
        const y = p.y + 10;
        ctx.damage.explode(
          x,
          y,
          a.radius,
          { amount: a.damage, tags: ['kinetic'], source: 'player', noTrigger: true, knockback: a.knockback, label: rt.key },
          { fx: false },
        );
        fx.ring(x, y, a.radius, { color: COLORS.dash, duration: 280, width: 5, fromRadius: 24 });
        fx.ring(x, y, a.radius * 0.7, { color: COLORS.shield, duration: 220, width: 3, fromRadius: 16 });
        fx.debris(x, p.feetY, { count: 8, color: COLORS.steel4, speed: 320 });
        fx.shake(0.005, 160);
        sfx.play('land_heavy', { volume: 0.9, x }, 60);
        sfx.play('explosion_small', { volume: 0.4, pitch: 0.7, x }, 60);
        return true;
      }
      case 'missiles': {
        let any: EnemyApi | null = null;
        try {
          any = ctx.enemies.nearest(p.x, p.y, 1200);
        } catch {
          any = null;
        }
        if (!any) return false;
        fireMissiles(p, a.count, a.damage);
        return true;
      }
      case 'status': {
        if (a.radius && a.radius > 0) {
          let n = 0;
          for (const e of enemyList(ctx)) {
            if (!targetable(e)) continue;
            if (Math.hypot(e.x - cx, e.y - cy) - e.radius > a.radius) continue;
            ctx.status.apply(e, a.status);
            n++;
          }
          fx.ring(cx, cy, a.radius, { color: statusColor(a.status.id), duration: 300, width: 4, fromRadius: 20 });
          return n > 0 || true;
        }
        const t = target ?? nearestTo(cx, cy, 220);
        if (!t) return false;
        ctx.status.apply(t, a.status);
        return true;
      }
      case 'shield': {
        const max = p.stats.maxShield;
        if (p.shield >= max) return false;
        const gained = Math.min(a.amount, max - p.shield);
        p.shield += gained;
        fx.floatText(p.x, p.y - 70, `+${Math.round(gained)} SHIELD`, { color: COLORS.shieldBar, size: 20 });
        fx.ring(p.x, p.y, 60, { color: COLORS.shield, duration: 260, width: 3, fromRadius: 90 });
        return true;
      }
      case 'heal': {
        if (p.hp >= p.stats.maxHp) return false;
        p.heal(a.amount);
        return true;
      }
      case 'cool': {
        const w = p.activeWeapon();
        if (!w || w.heat <= 0) return false;
        w.vent(a.amount);
        const m = p.muzzle();
        fx.vent(m.x, m.y, m.angle);
        return true;
      }
      case 'trail': {
        const path = data.path ?? { x0: p.x - 40 * p.facing, y0: p.y, x1: p.x, y1: p.y };
        // The dash path is the body centre; lay the trail along the ground line under it
        // (where the dash's own streak runs) instead of across the chest.
        const drop = Math.max(0, p.feetY - p.y - TRAIL_GROUND_LIFT);
        ctx.zones.trail(a.kind, path.x0, path.y0 + drop, path.x1, path.y1 + drop, {
          duration: a.duration,
          dps: a.dps,
          status: a.status ?? TRAIL_DEFAULT_STATUS[a.kind],
        });
        return true;
      }
      case 'gravity': {
        ctx.zones.add({ kind: 'gravity', owner: 'player', x: cx, y: cy, radius: a.radius, duration: a.duration, strength: a.strength, color: COLORS.gravity });
        return true;
      }
      case 'cooldown': {
        if (a.ability === 'signature') ctx.abilities.reduceSignatureCooldown(a.seconds);
        else p.reduceDashCooldown(a.seconds);
        return true;
      }
      case 'buff': {
        p.addBuff(`effect:${rt.key}`, [{ stat: a.stat, op: a.op, value: a.value }], a.duration);
        fx.ring(p.x, p.y, 70, { color: buffColor(a.stat), duration: 300, width: 3, fromRadius: 30 });
        return true;
      }
      case 'custom': {
        if (!warnedCustom.has(a.id)) {
          warnedCustom.add(a.id);
          console.warn(`[combat] custom effect action "${a.id}" is not supported (use the flag vocabulary)`);
        }
        return false;
      }
    }
    return false;
  }

  function fireMissiles(p: PlayerApi, count: number, damage: number): void {
    let left = { x: p.x - 14 * p.facing, y: p.y - 38 };
    let right = { x: p.x + 6 * p.facing, y: p.y - 40 };
    try {
      const r = p.view.attachWorld('shoulderR');
      const f = p.view.attachWorld('shoulderF');
      left = { x: r.x, y: r.y - 6 };
      right = { x: f.x, y: f.y - 6 };
    } catch {
      // view not ready: body-relative fallback
    }
    const n = Math.max(1, Math.round(count));
    for (let i = 0; i < n; i++) {
      const o = i % 2 === 0 ? left : right;
      const fan = (i - (n - 1) / 2) * 0.32;
      const spec: ProjectileSpecEx = {
        owner: 'player',
        x: o.x,
        y: o.y,
        angle: -Math.PI / 2 + p.facing * 0.35 + fan,
        speed: 860,
        launchSpeedFrac: 0.3,
        damage,
        tags: ['explosive', 'missile'],
        texture: TEX.projRocket,
        scale: 0.75,
        radius: 5,
        lifetime: 2.4,
        homing: { turnRate: 8.5, range: 950, delay: 0.16 },
        explode: { radius: 56, damage: damage * 0.5, tags: ['explosive', 'missile'] },
        knockback: 120,
        trail: 'rocket',
        tint: 0xffd2a0,
        noTrigger: true,
        label: 'micro_missile',
      };
      ctx.projectiles.spawn(spec);
    }
    fx.smoke(left.x, left.y, { count: 2, color: 0x4a4650, scale: 0.4 });
    sfx.play('shoot_rocket', { volume: 0.45, pitch: 1.2, x: p.x }, 80);
  }

  function nearestTo(x: number, y: number, r: number): EnemyApi | null {
    try {
      return ctx.enemies.nearest(x, y, r);
    } catch {
      return null;
    }
  }

  function dispatch(trigger: Trigger, data: TriggerData): void {
    if (destroyed) return;
    if (!built) rebuildList();
    const list = byTrigger.get(trigger);
    if (!list || list.length === 0) return;
    if (depth >= MAX_DEPTH) return;
    depth++;
    try {
      const t = now();
      for (const rt of list) {
        const e = rt.effect;
        if (e.requiresTag && !(data.info?.tags ?? []).includes(e.requiresTag)) continue;
        const cdEnd = cooldownUntil.get(rt.key);
        if (cdEnd !== undefined && t < cdEnd) continue;
        if (e.chance !== undefined && e.chance < 1 && !ctx.rng.chance(e.chance)) continue;
        let ok = false;
        try {
          ok = run(rt, e.action, data);
        } catch (err) {
          console.warn(`[combat] effect ${rt.key} failed`, err);
        }
        if (ok && e.cooldown && e.cooldown > 0) cooldownUntil.set(rt.key, t + e.cooldown);
      }
    } finally {
      depth--;
    }
  }

  const fromPlayerHit = (info: DamageInfo): boolean =>
    info.source === 'player' && !info.isDot && !info.secondary && !info.noTrigger;

  const api: EffectRuntimeApi = {
    hasFlag(f) {
      if (!built && !destroyed) rebuildList();
      return flags.has(f);
    },

    fire(trigger, data) {
      dispatch(trigger, data ?? {});
    },

    rebuild() {
      rebuildList();
      if (started) diffSynergies();
    },

    get activeSynergies() {
      if (!built && !destroyed) rebuildList();
      return synergies;
    },

    start() {
      started = true;
      rebuildList();
      diffSynergies();
      subs
        .on('inventory:changed', () => api.rebuild())
        .on('player:dash', (e) => dispatch('onDash', { x: e.x, y: e.y }))
        .on('player:dashEnd', (e) => dispatch('onDashEnd', { x: e.x1, y: e.y1, path: e }))
        .on('player:jump', (e) => dispatch('onJump', { x: e.x, y: e.y }))
        .on('player:land', (e) => {
          if (e.impact >= LAND_IMPACT) dispatch('onLand', { x: e.x, y: e.y });
        })
        .on('enemy:damaged', (e) => {
          if (e.enemy.kind === 'prop' || !fromPlayerHit(e.info)) return;
          dispatch('onHit', { target: e.enemy, info: e.info });
          if (e.crit) dispatch('onCrit', { target: e.enemy, info: e.info });
        })
        .on('enemy:killed', (e) => {
          if (e.enemy.kind === 'prop' || e.info.source !== 'player' || e.info.noTrigger) return;
          dispatch('onKill', { target: e.enemy, x: e.x, y: e.y, info: e.info });
        })
        .on('player:overheat', () => dispatch('onOverheat', {}))
        .on('player:ventEnd', () => dispatch('onVentEnd', {}))
        .on('player:damaged', (e) => {
          if (e.info.source === 'self') return;
          dispatch('onDamaged', { info: e.info });
        })
        .on('player:shieldBreak', () => dispatch('onShieldBreak', {}))
        .on('player:signature', () => dispatch('onSignature', {}))
        .on('player:ultimate', () => dispatch('onUltimate', {}))
        .on('weapon:fired', (e) => {
          if (!e.secondary) dispatch('onFire', { x: e.x, y: e.y });
        })
        .on('room:combatStart', () => dispatch('onRoomStart', {}))
        .on('room:cleared', () => dispatch('onRoomClear', {}));
    },

    update(dt) {
      if (destroyed || ticking.length === 0) return;
      if (!inCombat()) return;
      for (const rt of ticking) {
        rt.tickT -= dt;
        if (rt.tickT > 0) continue;
        rt.tickT += Math.max(0.1, rt.effect.interval ?? 1);
        dispatch1(rt);
      }
    },

    destroy() {
      destroyed = true;
      subs.dispose();
      byTrigger.clear();
      ticking.length = 0;
    },
  };

  /** Fire a single tick effect (respects its own cooldown / chance). */
  function dispatch1(rt: RtEffect): void {
    const e = rt.effect;
    const t = now();
    const cdEnd = cooldownUntil.get(rt.key);
    if (cdEnd !== undefined && t < cdEnd) return;
    if (e.chance !== undefined && e.chance < 1 && !ctx.rng.chance(e.chance)) return;
    let ok = false;
    try {
      ok = run(rt, e.action, {});
    } catch (err) {
      console.warn(`[combat] effect ${rt.key} failed`, err);
    }
    if (ok && e.cooldown && e.cooldown > 0) cooldownUntil.set(rt.key, t + e.cooldown);
  }

  return api;
}

function statusColor(id: string): number {
  switch (id) {
    case 'burn':
    case 'overheated':
      return COLORS.burn;
    case 'shock':
    case 'stun':
      return COLORS.electric;
    case 'slow':
      return 0x9fd8ff;
    default:
      return COLORS.precision;
  }
}

function buffColor(stat: string): number {
  if (stat === 'damage' || stat === 'fireRate' || stat === 'critChance') return COLORS.heat;
  if (stat === 'armor' || stat === 'damageTaken' || stat === 'maxShield') return COLORS.shield;
  return COLORS.ultBar;
}
